import { CalendarIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import RangeDatePicker from "../../shared/components/DatePicker/RangeDatePicker";
import AgencyMemberSelect from "../../shared/components/AgencyMemberSelect";
import ContractTypeSelect from "../../shared/components/ContractTypeSelect";
import PatientStatusSelect from "../../shared/components/PatientStatusSelect";
import { createFilters } from "../../shared/hooks/useFilters";
import useApi from "../../shared/hooks/useApi";
import { EntitySelect } from "../workflow/components/EntityFormControl";
import { AuthorizationUtilizationDashboard } from "./authorization-utilization.types";
import {
  AuthorizedHours,
  AuthTypeSelect,
  deriveUtilizationState,
  formatHours,
  formatPercent,
  UtilizationBar,
  UtilizationRemaining,
} from "./authorization-utilization.utils";
import {
  SharedUtilizationFilters,
  useSharedUtilizationFilters,
} from "./useSharedUtilizationFilters";

const { createRangeDatePickerFilter } = createFilters<SharedUtilizationFilters>();

function MetricCard(props: { label: string; value: string; helpText?: string; accent?: boolean }) {
  return (
    <Stat bg={props.accent ? "orange.50" : "gray.50"} borderRadius="md" px={4} py={3}>
      <StatLabel color={props.accent ? "orange.700" : "gray.600"}>{props.label}</StatLabel>
      <StatNumber color={props.accent ? "orange.800" : undefined}>{props.value}</StatNumber>
      {props.helpText !== undefined ? <StatHelpText mb={0}>{props.helpText}</StatHelpText> : null}
    </Stat>
  );
}

export default function UtilizationDashboardTab() {
  const [filters, setFilters] = useSharedUtilizationFilters();
  const { queries } = useApi();

  // Memoized so the queryKey reference is stable across renders; otherwise
  // usePageFetchingObserver's effect re-fires every render and the page
  // loading indicator never settles.
  const queryOptions = useMemo(
    () =>
      queries.authorizationUtilization.dashboard({
        from: filters.from,
        to: filters.to,
        officeIds: filters.officeIds,
        contractTypeIds: filters.contractTypeIds,
        coordinatorIds: filters.coordinatorIds,
        periodTypes: filters.periodTypes,
        patientStatuses: filters.patientStatuses,
      }),
    [
      queries,
      filters.from,
      filters.to,
      filters.officeIds,
      filters.contractTypeIds,
      filters.coordinatorIds,
      filters.periodTypes,
      filters.patientStatuses,
    ]
  );

  // Auto-load only once the user has chosen a real filter (not just the default
  // date range) — avoids loading the whole agency on first open.
  const hasFilters =
    (filters.officeIds?.length ?? 0) > 0 ||
    (filters.contractTypeIds?.length ?? 0) > 0 ||
    (filters.coordinatorIds?.length ?? 0) > 0 ||
    (filters.periodTypes?.length ?? 0) > 0 ||
    (filters.patientStatuses?.length ?? 0) > 0;

  const query = useQuery({
    ...queryOptions,
    select: (data: AuthorizationUtilizationDashboard) => data,
    enabled: hasFilters,
  });

  const fromToFilter = createRangeDatePickerFilter({
    label: "Date range",
    description: "Shows authorizations whose period overlaps the selected date range",
    startDate: { name: "from", value: filters.from ? LocalDate.parse(filters.from) : null },
    endDate: { name: "to", value: filters.to ? LocalDate.parse(filters.to) : null },
    onChange: (key, value) => setFilters((prev) => ({ ...prev, [key]: value?.toString() })),
  });

  const data = query.data;

  return (
    <Box p={6}>
      <Flex align="center" gap={3} mb={6} position="relative" wrap="wrap" zIndex={20}>
        <Tooltip hasArrow label={fromToFilter.description} placement="bottom-start">
          <InputGroup width="xs">
            <RangeDatePicker {...fromToFilter} inputProps={{ width: "full" }} />
            <InputRightElement>
              <CalendarIcon color="gray.400" />
            </InputRightElement>
          </InputGroup>
        </Tooltip>

        <EntitySelect
          input={{ type: "entity", entity: "Office" }}
          label="Office"
          multiple={true}
          renderUnselected="Office"
          value={filters.officeIds ?? null}
          onChange={(x) => setFilters((prev) => ({ ...prev, officeIds: x ?? undefined }))}
        />

        <ContractTypeSelect
          label="Contract"
          multiple={true}
          value={filters.contractTypeIds ?? null}
          onChange={(x) => setFilters((prev) => ({ ...prev, contractTypeIds: x ?? undefined }))}
        />

        <AuthTypeSelect
          value={filters.periodTypes ?? null}
          onChange={(x) => setFilters((prev) => ({ ...prev, periodTypes: x ?? undefined }))}
        />

        <AgencyMemberSelect
          label="Coordinator"
          multiple={true}
          value={filters.coordinatorIds ?? null}
          onChange={(x) => setFilters((prev) => ({ ...prev, coordinatorIds: x ?? undefined }))}
        />

        <PatientStatusSelect
          label="Patient status"
          multiple={true}
          value={filters.patientStatuses ?? null}
          onChange={(x) => setFilters((prev) => ({ ...prev, patientStatuses: x ?? undefined }))}
        />
      </Flex>

      {!hasFilters ? (
        <Flex justify="center" py={16}>
          <Text color="gray.500">
            Choose an office, contract, or other filter to load utilization.
          </Text>
        </Flex>
      ) : data === undefined ? (
        <Flex justify="center" py={12}>
          <Spinner color="blue.500" size="lg" thickness="3px" />
        </Flex>
      ) : (
        <>
          <Box bg="gray.50" borderRadius="xl" mb={6} p={6}>
            <Flex align="flex-end" gap={2} justify="space-between" mb={3} wrap="wrap">
              <Box>
                <Text
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="medium"
                  letterSpacing="wide"
                  textTransform="uppercase"
                >
                  Scheduled utilization
                </Text>
                <Text color="gray.500" fontSize="sm" mt={1}>
                  {formatHours(data.scheduledMinutes)} h scheduled of{" "}
                  {formatHours(data.authorizedMinutes)} h authorized
                </Text>
              </Box>
              <Text
                color={
                  data.scheduledUtilization !== null && data.scheduledUtilization > 1
                    ? "red.600"
                    : "blue.700"
                }
                fontSize="5xl"
                fontWeight="bold"
                lineHeight="1"
              >
                {formatPercent(data.scheduledUtilization)}
              </Text>
            </Flex>

            <Box bg="orange.100" borderRadius="full" h="16px" overflow="hidden" position="relative">
              <Box
                bg={
                  data.scheduledUtilization !== null && data.scheduledUtilization > 1
                    ? "red.400"
                    : "blue.400"
                }
                h="100%"
                left={0}
                position="absolute"
                top={0}
                w={`${
                  data.scheduledUtilization === null
                    ? 0
                    : Math.min(100, Math.round(data.scheduledUtilization * 100))
                }%`}
              />
            </Box>

            <Flex justify="space-between" mt={2}>
              <Text color="blue.700" fontSize="xs" fontWeight="medium">
                {formatHours(data.scheduledMinutes)} h scheduled
              </Text>
              <Text color="orange.700" fontSize="xs" fontWeight="medium">
                {formatHours(data.unscheduledMinutes)} h unscheduled
              </Text>
            </Flex>

            {data.authorizedReductionMinutes > 0 ? (
              <Text color="gray.500" fontSize="xs" mt={2}>
                Authorized adjusted &minus;{(data.authorizedReductionMinutes / 60).toFixed(1)} h for
                on-hold / hospitalized time in range
              </Text>
            ) : null}
          </Box>

          <SimpleGrid columns={{ base: 2, md: 4 }} mb={8} spacing={3}>
            <MetricCard label="Authorized" value={`${formatHours(data.authorizedMinutes)} h`} />
            <MetricCard
              helpText={`${formatPercent(data.scheduledUtilization)} of authorized`}
              label="Scheduled"
              value={`${formatHours(data.scheduledMinutes)} h`}
            />
            <MetricCard
              accent={true}
              helpText="recoverable"
              label="Unscheduled"
              value={`${formatHours(data.unscheduledMinutes)} h`}
            />
            <MetricCard
              label="Over-allocated"
              value={`${formatHours(data.overAllocatedMinutes)} h`}
            />
          </SimpleGrid>

          <Text fontSize="md" fontWeight="medium" mb={3}>
            By office and contract
          </Text>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Office</Th>
                  <Th>Contract</Th>
                  <Th isNumeric>Auth (h)</Th>
                  <Th width="180px">Scheduled</Th>
                  <Th isNumeric>Unsched (h)</Th>
                  <Th isNumeric>Under</Th>
                  <Th isNumeric>Full</Th>
                  <Th isNumeric>Over</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.byOfficeContract.map((row) => (
                  <Tr key={`${row.officeId}:${row.contractTypeId}`}>
                    <Td>{row.officeName}</Td>
                    <Td color="gray.600">{row.contractTypeName}</Td>
                    <Td isNumeric>
                      <AuthorizedHours
                        authorizedMinutes={row.authorizedMinutes}
                        authorizedReductionMinutes={row.authorizedReductionMinutes}
                      />
                    </Td>
                    <Td>
                      <UtilizationBar
                        authorizedMinutes={row.authorizedMinutes}
                        scheduledMinutes={row.scheduledMinutes}
                        state={deriveUtilizationState(row.scheduledMinutes, row.authorizedMinutes)}
                      />
                    </Td>
                    <Td isNumeric>
                      <UtilizationRemaining
                        authorizedMinutes={row.authorizedMinutes}
                        scheduledMinutes={row.scheduledMinutes}
                      />
                    </Td>
                    <Td isNumeric>{row.patientsUnderAllocated}</Td>
                    <Td isNumeric>{row.patientsFullyAllocated}</Td>
                    <Td isNumeric>{row.patientsOverAllocated}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
